// src/modules/app-views/products/pages/Document/tabs/AiInfoTab/AiMetaSection.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { Flex, Descriptions, Collapse, Tag, theme } from 'antd';
import { RobotOutlined } from '@ant-design/icons';
import translateX from 'utils/translateX';
import dayjs from 'dayjs';

// *******************************************************************************
// HELPERS
// *******************************************************************************
// Safely render value that might be an object {uid, label} or a string
function renderValue(value) {
  if (!value) return null;
  if (typeof value === 'object' && value.label) return value.label;
  if (typeof value === 'object') return JSON.stringify(value);
  return value;
}

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const AiMetaSection = ({ data, isMobile }) => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const { token } = theme.useToken();
  const ai = data?.aiCatalogMeta;

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. LOCAL VARIABLES
  // ─────────────────────────────────────────────────────────────────────────────
  const hasData = ai && ai.catalogedByAI;

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. UI
  // ─────────────────────────────────────────────────────────────────────────────
  if (!hasData) return null;

  return (
    <Collapse
      className="collapsed-content-fit"
      collapsible="icon"
      defaultActiveKey={['ai-meta']}
      style={{ marginBottom: 16 }}
      items={[
        {
          key: 'ai-meta',
          label: (
            <Flex align="center" gap={8}>
              <RobotOutlined style={{ color: token.colorPrimary }} />
              <span style={{ fontWeight: 600 }}>{translateX('ai_cataloging_info')}</span>
            </Flex>
          ),
          showArrow: false,
          children: (
            <Descriptions
              bordered
              size="small"
              column={1}
              layout={isMobile ? 'vertical' : 'horizontal'}
              styles={{ label: { fontWeight: 500 } }}
            >
              <Descriptions.Item label={translateX('provider')}>
                {renderValue(ai.provider)?.toUpperCase()}
              </Descriptions.Item>
              <Descriptions.Item label={translateX('model')}>
                {renderValue(ai.model)}
              </Descriptions.Item>
              <Descriptions.Item label="Tokens">
                {ai.totalTokens?.toLocaleString()} ({translateX('input')}: {ai.inputTokens?.toLocaleString()}, {translateX('output')}: {ai.outputTokens?.toLocaleString()})
              </Descriptions.Item>
              <Descriptions.Item label={translateX('response_time')}>
                {(ai.responseTimeMs / 1000).toFixed(1)}s
              </Descriptions.Item>
              <Descriptions.Item label={translateX('credits_debited')}>
                {ai.finalCostBRL ? Math.ceil(ai.finalCostBRL * 10) : '-'} {translateX('credits')}
              </Descriptions.Item>
              <Descriptions.Item label={translateX('cataloged_at')}>
                {ai.catalogedAt ? dayjs(ai.catalogedAt).format('DD/MM/YYYY HH:mm:ss') : '-'}
              </Descriptions.Item>
              <Descriptions.Item label={translateX('manually_reviewed')}>
                {ai.manuallyReviewed ? (
                  <Tag color="green">{translateX('yes')}</Tag>
                ) : (
                  <Tag color="orange">{translateX('no')}</Tag>
                )}
              </Descriptions.Item>
            </Descriptions>
          ),
        },
      ]}
    />
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default AiMetaSection;
